import React, { createContext, useContext, useEffect } from 'react';
import {
    useGetListViewMilkScheduleDemandItems,
    useGetListViewMilkScheduleSupplyItems,
    useGetListViewTotals,
    getScheduleDemandItem,
    getListViewMilkScheduleSupplyItems,
    // wrap
} from './Schedule.queries';
import { useScheduleSettings } from './ScheduleSettingsStore';
import useSSE from '../../../../hooks/useServerSentEvents';
import useBatching from '../../../../hooks/useBatching';
import { updateSupplyItemCacheRowById, updateDemandItemCacheRowById } from './common/utils';
import { useQueryClient } from '@tanstack/react-query';
import useSupplyItemsSSE from './useSupplyItemsSSE';
import { useScheduleSelection } from './ScheduleSelectionContext';
import { getUser, getRoleRestriction } from '../../../../../utils';

const ScheduleContext = createContext({});

export function ScheduleProvider({ children, currentTab }) {
    const user = getUser();
    const roleRestriction = getRoleRestriction(user);
    const { settings, filters } = useScheduleSettings();
    const { order } = filters;
    const { flashRows } = useScheduleSelection();
    const { batchedNewSupplyItems, batchedUpdateSupplyItems, batchedUpdateManySupplyItems, status: supplySSEStatus } = useSupplyItemsSSE();
    const { data: demandItemUpdate, status: demandSSEStatus } = useSSE('schedule-demand-items');
    const { batchedItems: batchedInvalidateScheduleTotals, addItems: triggerScheduleTotalsInvalidation } = useBatching(60000);
    const queryClient = useQueryClient();

    const refreshSupplyItems = async (documentIds, queryKeyTag, fetch) => {
        await updateSupplyItemCacheRowById({ queryClient, queryKeyTag, fetch, documentIds, order });
        flashRows(documentIds);
    };

    const refreshDemandItems = async (documentIds, queryKeyTag, fetch, groupBy) => {
        await updateDemandItemCacheRowById({ queryClient, queryKeyTag, fetch, documentIds, groupBy });
        flashRows(documentIds);
    };

    useEffect(() => {
        if (batchedNewSupplyItems.length) {
            flashRows(batchedNewSupplyItems);
            queryClient.invalidateQueries({ queryKey: ['list-view-supply-items'] });
            triggerScheduleTotalsInvalidation(['list-view-schedule-totals']);
        }
    }, [batchedNewSupplyItems]);

    const fetchSupplyItems = (ids) => {
        return queryClient.fetchQuery({
            queryKey: ['schedule-supply-items', ids],
            queryFn: getListViewMilkScheduleSupplyItems({ queryParams: { _id: ids, ...filters, ...roleRestriction } }),
        });
    };
    useEffect(() => {
        if (batchedUpdateManySupplyItems.length) {
            queryClient.invalidateQueries(['schedule-supply-items']);
            refreshSupplyItems(batchedUpdateManySupplyItems, 'list-view-supply-items', () => fetchSupplyItems(batchedUpdateManySupplyItems));
            triggerScheduleTotalsInvalidation(['list-view-schedule-totals']);
        }
    }, [batchedUpdateManySupplyItems]);

    useEffect(() => {
        if (batchedUpdateSupplyItems.length) {
            queryClient.invalidateQueries(['schedule-supply-items']);
            refreshSupplyItems(batchedUpdateSupplyItems, 'list-view-supply-items', () => fetchSupplyItems(batchedUpdateSupplyItems));
            triggerScheduleTotalsInvalidation(['list-view-schedule-totals']);
        }
    }, [batchedUpdateSupplyItems]);

    const fetchDemandItems = (ids) => {
        return queryClient.fetchQuery({
            queryKey: ['schedule-demand-items', ids],
            queryFn: getScheduleDemandItem({ queryParams: { _ids: ids, ...filters, ...roleRestriction } }),
        });
    };
    useEffect(() => {
        if (demandItemUpdate?._ids?.length) {
            if (demandItemUpdate.method === 'save') {
                queryClient.invalidateQueries({ queryKey: ['list-view-demand-items'] });
                flashRows(demandItemUpdate?._ids);
            } else {
                queryClient.invalidateQueries(['schedule-demand-items']);
                refreshDemandItems(demandItemUpdate?._ids, 'list-view-demand-items', () => fetchDemandItems(demandItemUpdate?._ids), 'date');
            }
            triggerScheduleTotalsInvalidation(['list-view-schedule-totals']);
        }
    }, [demandItemUpdate]);

    useEffect(() => {
        if (batchedInvalidateScheduleTotals.length) {
            queryClient.invalidateQueries({ queryKey: ['list-view-schedule-totals'] });
        }
    }, [batchedInvalidateScheduleTotals]);

    const supplyItems = useGetListViewMilkScheduleSupplyItems({ queryParams: { ...filters, selectedDay: settings.selectedDay }, enabled: currentTab === 0 });
    const demandItems = useGetListViewMilkScheduleDemandItems({ queryParams: { ...filters, selectedDay: settings.selectedDay }, enabled: currentTab === 1 });
    const dashboardTotals = useGetListViewTotals({ queryParams: filters, enabled: settings.showDashboard });

    return (
        <ScheduleContext.Provider
            value={{
                currentTab,
                supplyItems,
                demandItems,
                dashboardTotals,
                supplySSEStatus,
                demandSSEStatus,
            }}
        >
            {children}
        </ScheduleContext.Provider>
    );
}

export const useScheduleData = () => {
    const context = useContext(ScheduleContext);
    if (context === undefined) {
        throw new Error('useSchedulingProvider must be used within a ScheduleProvider');
    }
    return context;
};
